import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  authAPI,
  adminAPI,
  getToken,
  setToken,
  removeToken,
  removeExternalSession,
  getExternalAccessToken,
  getExternalRefreshToken,
  setExternalSession,
  PublicUser,
} from '../utils/api';

type User = PublicUser;

const ADMIN_SESSION_KEY = 'flypix:admin_impersonation_backup';

type AdminSessionBackup = {
  token: string;
  external_access_token: string | null;
  external_refresh_token: string | null;
  external_refresh_expires_at: string | null;
  admin: User;
};

function readAdminBackup(): AdminSessionBackup | null {
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdminSessionBackup;
  } catch {
    return null;
  }
}

function writeAdminBackup(backup: AdminSessionBackup): void {
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(backup));
}

function clearAdminBackup(): void {
  localStorage.removeItem(ADMIN_SESSION_KEY);
}

/** Активный вход под другим пользователем: есть бэкап и текущий токен уже не админский. */
function isActiveImpersonation(): boolean {
  const backup = readAdminBackup();
  const token = getToken();
  return !!backup?.token && !!token && token !== backup.token;
}

/** Бэкап остался после неудачного входа — токен не менялся. */
function isStaleImpersonationBackup(): boolean {
  const backup = readAdminBackup();
  const token = getToken();
  return !!backup?.token && !!token && token === backup.token;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isImpersonating: boolean;
  login: (email: string, password: string) => Promise<void>;
  requestRegistrationCode: (
    name: string,
    email: string,
    password: string,
    password_confirmation: string
  ) => Promise<{ verification_id?: number; expires_at?: string; registered?: boolean }>;
  verifyRegistrationCode: (verification_id: number, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (user: User | null) => void;
  impersonateUser: (userId: number) => Promise<void>;
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(() => isActiveImpersonation());

  // Проверка авторизации при загрузке
  useEffect(() => {
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const restoreAdminFromBackup = async (): Promise<boolean> => {
      const backup = readAdminBackup();
      if (!backup?.token) {
        return false;
      }

      setToken(backup.token);
      if (backup.external_access_token && backup.external_refresh_token) {
        setExternalSession(
          backup.external_access_token,
          backup.external_refresh_token,
          backup.external_refresh_expires_at
        );
      } else {
        removeExternalSession();
      }
      clearAdminBackup();
      setIsImpersonating(false);

      try {
        const response = await authAPI.getUser();
        if (response.success && response.user) {
          setUser(response.user);
          return true;
        }
      } catch (error) {
        console.error('Не удалось восстановить сессию админа:', error);
      }

      return false;
    };

    const checkAuth = async () => {
      if (isStaleImpersonationBackup()) {
        clearAdminBackup();
        setIsImpersonating(false);
      }

      const token = getToken();
      if (token) {
        let lastError: unknown = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const response = await authAPI.getUser();
            if (response.success && response.user) {
              setUser(response.user);
              lastError = null;
              break;
            }
            lastError = new Error('empty user');
          } catch (error) {
            lastError = error;
            const status = (error as { status?: number; response?: { code?: string } })?.status;
            const code = (error as { response?: { code?: string } })?.response?.code;
            if (status === 429 || status === 502 || status === 503 || status === 504) {
              await sleep(400 * (attempt + 1));
              continue;
            }
            if (status === 401 && code === 'EXTERNAL_SESSION_EXPIRED') {
              await sleep(300);
              continue;
            }
            if (status === 401 && readAdminBackup()) {
              if (await restoreAdminFromBackup()) {
                lastError = null;
                break;
              }
            }
            if (status === 401) {
              removeToken();
              removeExternalSession();
              clearAdminBackup();
              setIsImpersonating(false);
            }
            break;
          }
        }
        if (lastError) {
          console.error('Ошибка проверки авторизации:', lastError);
        }
      }
      setIsImpersonating(isActiveImpersonation());
      setLoading(false);
    };

    checkAuth();
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await authAPI.getUser();
      if (response.success && response.user) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Ошибка обновления профиля:', error);
    }
  }, []);

  useEffect(() => {
    const refresh = () => { if (getToken()) void refreshUser(); };
    window.addEventListener('flypix:email-verification-required', refresh);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 30000);
    return () => { window.removeEventListener('flypix:email-verification-required', refresh); window.clearInterval(timer); };
  }, [refreshUser]);

  // Актуальный статус подписки при возврате на вкладку (без удаления данных)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && getToken()) {
        refreshUser();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    clearAdminBackup();
    setIsImpersonating(false);
    const response = await authAPI.login(email, password);
    if (response.success && response.user) {
      if (response.user.email_verification_required) removeExternalSession();
      setUser(response.user);
    } else {
      throw new Error('Ошибка входа');
    }
  };

  const requestRegistrationCode = async (
    name: string,
    email: string,
    password: string,
    password_confirmation: string
  ) => {
    const response = await authAPI.requestRegistrationCode(name, email, password, password_confirmation);
    if (response.success && response.user && response.token) {
      clearAdminBackup();
      setIsImpersonating(false);
      setUser(response.user);
      return { registered: true };
    }
    if (response.success && response.verification_id) {
      return { verification_id: response.verification_id, expires_at: response.expires_at };
    }
    throw new Error(response.message || 'Ошибка отправки кода');
  };

  const verifyRegistrationCode = async (verification_id: number, code: string) => {
    clearAdminBackup();
    setIsImpersonating(false);
    const response = await authAPI.verifyRegistrationCode(verification_id, code);
    if (response.success && response.user) {
      setUser(response.user);
    } else {
      throw new Error('Ошибка подтверждения кода');
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Ошибка выхода:', error);
    } finally {
      setUser(null);
      removeToken();
      removeExternalSession();
      clearAdminBackup();
      setIsImpersonating(false);
    }
  };

  const impersonateUser = async (userId: number) => {
    const token = getToken();
    if (!token || !user) {
      throw new Error('Нет активной сессии администратора');
    }

    const backup: AdminSessionBackup = {
      token,
      external_access_token: getExternalAccessToken(),
      external_refresh_token: getExternalRefreshToken(),
      external_refresh_expires_at: localStorage.getItem('external_refresh_expires_at'),
      admin: user,
    };

    try {
      const response = await adminAPI.impersonate(userId);
      if (!response.success || !response.user) {
        throw new Error(response.message || 'Не удалось войти под пользователем');
      }

      writeAdminBackup(backup);
      setUser(response.user);
      setIsImpersonating(true);
    } catch (error) {
      clearAdminBackup();
      setIsImpersonating(false);
      throw error;
    }
  };

  const stopImpersonating = async () => {
    const backup = readAdminBackup();
    if (!backup?.token) {
      clearAdminBackup();
      setIsImpersonating(false);
      throw new Error('Сессия администратора не найдена');
    }

    const impersonationToken = getToken();

    // Сразу восстанавливаем админа — logout() с админским токеном убивал бы его сессию.
    setToken(backup.token);
    if (backup.external_access_token && backup.external_refresh_token) {
      setExternalSession(
        backup.external_access_token,
        backup.external_refresh_token,
        backup.external_refresh_expires_at
      );
    } else {
      removeExternalSession();
    }

    clearAdminBackup();
    setIsImpersonating(false);
    setUser(backup.admin);

    // По желанию отзываем только impersonation-токен (не трогаем админа).
    if (impersonationToken && impersonationToken !== backup.token) {
      try {
        await authAPI.logoutWithToken(impersonationToken);
      } catch {
        // сессия пользователя могла уже истечь
      }
    }

    try {
      const response = await authAPI.getUser();
      if (response.success && response.user) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Не удалось восстановить сессию админа:', error);
      throw new Error('Не удалось вернуться в админку — войдите снова');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        isAdmin: !!user?.is_admin,
        isImpersonating,
        login,
        requestRegistrationCode,
        verifyRegistrationCode,
        logout,
        refreshUser,
        setUser,
        impersonateUser,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
